import { useEffect } from 'react';

const MAX_ANCESTOR_DEPTH = 40;

/**
 * Walks up from `startEl` and sets scrollTop to 0 on ancestors that scroll vertically
 * (e.g. nested modals / flex columns with overflow-y: auto). Window scroll alone misses those.
 *
 * @param {Element | null | undefined} startEl
 */
export const scrollOverflowAncestorsToTop = (startEl) => {
  if (!startEl || typeof startEl !== 'object') return;
  let el = startEl;
  let depth = 0;
  while (el && depth < MAX_ANCESTOR_DEPTH) {
    const style = window.getComputedStyle(el);
    const oy = style.overflowY;
    if (
      (oy === 'auto' || oy === 'scroll' || oy === 'overlay') &&
      el.scrollHeight > el.clientHeight + 1
    ) {
      el.scrollTop = 0;
    }
    el = el.parentElement;
    depth += 1;
  }
};

const runWindowScrollToTop = (smooth) => {
  window.scrollTo(0, 0);

  if (document.documentElement) {
    document.documentElement.scrollTop = 0;
  }
  if (document.body) {
    document.body.scrollTop = 0;
  }

  window.scrollTo({
    top: 0,
    left: 0,
    behavior: smooth ? 'smooth' : 'instant',
  });
};

/**
 * Custom hook that scrolls to the top of the page when the component mounts
 * @param {Object} options - Configuration options
 * @param {boolean} options.smooth - Whether to use smooth scrolling (default: false)
 * @param {number} options.delay - Delay in milliseconds before scrolling (default: 0)
 * @param {import('react').RefObject<Element | null>} options.rootRef - Optional root node; scrollable ancestors from here are reset (use inside overflow containers)
 * @param {Array} options.dependencies - Additional dependencies to trigger scroll (default: [])
 */
export const useScrollToTop = (options = {}) => {
  const { smooth = false, delay = 0, rootRef, dependencies = [] } = options;

  useEffect(() => {
    const scrollToTop = () => {
      runWindowScrollToTop(smooth);
      if (rootRef?.current) {
        scrollOverflowAncestorsToTop(rootRef.current);
      }
    };

    if (delay > 0) {
      const timeoutId = setTimeout(scrollToTop, delay);
      return () => clearTimeout(timeoutId);
    }

    scrollToTop();
    const nextTickTimeout = setTimeout(scrollToTop, 0);
    return () => clearTimeout(nextTickTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- callers pass dependency list via `dependencies`
  }, [smooth, delay, rootRef, ...dependencies]);
};

/**
 * Higher Order Component that wraps a component and scrolls to top when it renders
 * @param {React.Component} WrappedComponent - The component to wrap
 * @param {Object} scrollOptions - Options for scrolling behavior
 * @returns {React.Component} - Enhanced component with scroll-to-top functionality
 */
export const withScrollToTop = (WrappedComponent, scrollOptions = {}) => {
  const ScrollToTopWrapper = (props) => {
    useScrollToTop(scrollOptions);
    return <WrappedComponent {...props} />;
  };

  // Set display name for better debugging
  ScrollToTopWrapper.displayName = `withScrollToTop(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;

  return ScrollToTopWrapper;
};

/**
 * Utility function to programmatically scroll to top
 * @param {Object} options - Scroll options
 * @param {boolean} options.smooth - Whether to use smooth scrolling (default: false)
 */
export const scrollToTop = (options = {}) => {
  const { smooth = false, rootEl } = options;
  runWindowScrollToTop(smooth);
  if (rootEl) {
    scrollOverflowAncestorsToTop(rootEl);
  }
};

/**
 * Component that can be used to trigger scroll to top on render
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render
 * @param {Object} props.scrollOptions - Options for scrolling behavior
 */
export const ScrollToTopOnMount = ({ children, scrollOptions = {} }) => {
  useScrollToTop(scrollOptions);
  return children || null;
};

export default useScrollToTop;
